import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import { createPayroll, updatePayroll } from "../../store/thunks/payrollThunk";

export default function CustomPayrollModal({
  clinician,
  selectedPeriod,
  isOpen,
  onClose,
  editingPayroll,
  onUpdateSuccess,
}) {
  const [appointment, setAppointment] = useState("");
  const [clientOrCompany, setClientOrCompany] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [payrollAmount, setPayrollAmount] = useState("");
  const [description, setDescription] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  // Reset form when modal opens/closes or editingPayroll changes
  useEffect(() => {
    if (isOpen) {
      if (editingPayroll) {
        setClientOrCompany(editingPayroll.clientOrCompany || "");
        setPayrollAmount(editingPayroll.payrollAmount?.toString() || "");
        setDescription(editingPayroll.description || "");
      } else {
        setClientOrCompany("");
        setSelectedClient(null);
        setPayrollAmount("");
        setDescription("");
      }
      setSuccessMessage(null);
      setErrorMessage(null);
    }
  }, [isOpen, editingPayroll]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    const payrollData = {
      appointmentId: appointment ? appointment._id : null,
      clinicianId: clinician._id,
      clientOrCompany: clientOrCompany,
      payrollAmount: payrollAmount ? parseFloat(payrollAmount) : 0,
      description: description || "",
      isCustom: true,
      status: "Pending",
      payPeriod: selectedPeriod.payPeriodNumber,
      payPeriodDates: selectedPeriod.includeDates,
      submitBy: selectedPeriod.submissionDate,
      payBy: selectedPeriod.paymentDate,
    };

    try {
      if (editingPayroll) {
        console.log("Updating payroll with this data", payrollData);
        const response = await dispatch(
          updatePayroll({
            id: editingPayroll._id,
            updateData: payrollData,
          })
        ).unwrap();
        setSuccessMessage(response.message || "Payroll updated successfully!");
      } else {
        console.log("Creating new payroll with this data", payrollData);
        const response = await dispatch(createPayroll(payrollData)).unwrap();
        setSuccessMessage(response.message || "Custom payroll item created");
      }

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage(
          error.message || "Failed to process payroll. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-start sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white shadow-lg transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="px-3 py-3 border-b border-gray-100">
                  <Dialog.Title className="text-sm font-medium text-gray-900">
                    {editingPayroll
                      ? "Edit Custom Payroll Item"
                      : "Add Custom Payroll Item"}
                  </Dialog.Title>
                  <p className="text-xs text-gray-500">
                    For pay period {selectedPeriod.payPeriodNumber}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="p-3 space-y-3">
                  <div>
                    <div className="text-xs font-medium text-gray-700">
                      Clinician
                    </div>
                    <div className="mt-1">
                      <p className="text-sm text-gray-900">
                        {clinician.firstName} {clinician.lastName}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-700">
                      Client
                    </div>
                    <div className="mt-1.5 space-y-1.5">
                      <SearchDropdown
                        prefix=":client:"
                        displayFields={[
                          "firstName",
                          "lastName",
                          "email",
                          "phoneNumbers",
                        ]}
                        onSelection={(client) => {
                          setClientOrCompany(client._id);
                          setSelectedClient(client);
                        }}
                        placeholder={"Search for an existing client"}
                        initialValue={clientOrCompany}
                      />
                      {selectedClient && (
                        <div className="inline-flex items-center px-2 py-1 bg-gray-50 rounded-sm border border-gray-100 text-xs">
                          <span className="text-gray-500">Selected:</span>
                          <span className="ml-1 font-medium text-gray-900">
                            {selectedClient.firstName} {selectedClient.lastName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-700">
                      Amount
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Input the exact amount - this is not affected by the
                      default pay percentage.
                    </p>
                    <div className="mt-1.5">
                      <input
                        id="amount"
                        name="amount"
                        type="number"
                        step="0.01"
                        value={payrollAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            setPayrollAmount(value);
                          }
                        }}
                        className="block w-full rounded-sm border-0 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-gray-300 sm:leading-6"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium text-gray-700">
                      Description
                    </div>
                    <div className="mt-1.5">
                      <textarea
                        id="description"
                        name="description"
                        rows={2}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="block w-full rounded-sm border-0 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-gray-300 sm:leading-6"
                      />
                    </div>
                  </div>

                  {(successMessage || errorMessage || isLoading) && (
                    <div className="mt-2 text-xs">
                      {successMessage && (
                        <div className="flex items-center text-green-600">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                          {successMessage}
                        </div>
                      )}
                      {errorMessage && (
                        <div className="flex items-center text-red-600">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                            />
                          </svg>
                          {errorMessage}
                        </div>
                      )}
                      {isLoading && (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="animate-spin w-3 h-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex justify-end gap-x-1.5 border-t border-gray-100 pt-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center justify-center rounded-sm px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-300">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center rounded-sm px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading}>
                      {isLoading
                        ? "Processing..."
                        : editingPayroll
                        ? "Update Item"
                        : "Add Item"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
              </Dialog>
    </Transition.Root>
  );
}
